import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, EntityId, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { ZapierApp } from '@sendhub/integrations/api-client-typescript-axios'
import { RootState } from '../root'
import { flatMap } from 'lodash'
import { integrationsApi } from '../../services/integrations-api'

export const ZAPIER_APPS_FEATURE_KEY = 'zapierApps'

export interface ZapierAppsState extends EntityState<Readonly<ZapierApp>> {
  loadingStatus: 'not loaded' | 'loading' | 'loaded' | 'error'
  error?: string | null
}

export const zapierAppsAdapter = createEntityAdapter<Readonly<ZapierApp>>({ selectId: (e) => e.slug })

/**
 * Export an effect using createAsyncThunk from
 * the Redux Toolkit: https://redux-toolkit.js.org/api/createAsyncThunk
 *
 * e.g.
 * ```
 * import React, { useEffect } from 'react';
 * import { useDispatch } from 'react-redux';
 *
 * // ...
 *
 * const dispatch = useDispatch();
 * useEffect(() => {
 *   dispatch(fetchZapierApps())
 * }, [dispatch]);
 * ```
 */
export const fetchZapierApps = createAsyncThunk('zapierApps/fetchStatus', async (_, thunkAPI) => {
  const PAGE_SIZE = 100
  return integrationsApi.listZapierApps(PAGE_SIZE).then((firstResp) => {
    thunkAPI.dispatch(zapierAppsSlice.actions.setMany(firstResp.data.objects))
    ;[...Array(firstResp.data.pages - 1).keys()].map((x) =>
      // Add 1 since it's not zero-based and another 1 since we already got the first page
      integrationsApi.listZapierApps(PAGE_SIZE, x + 2).then((resp) => thunkAPI.dispatch(zapierAppsSlice.actions.setMany(resp.data.objects)))
    )
  })
  // await Promise.all()

  // for await (const x of [...Array(firstResp.data.pages - 1).keys()]) {
  //   const page = x + 2 // Add 1 since it's not zero-based and another 1 since we already got the first page
  //   const resp = await zapierApi.listZapierApps(100, page)
  //   thunkAPI.dispatch(zapierAppsSlice.actions.setMany(resp.data.objects))
  // }

  // return zapierApi.listZapierApps().then((firstResp) => {
  //   const firstPage = firstResp.data.objects
  //   return Promise.all(
  //     [...Array(firstResp.data.pages - 1).keys()].map((x) =>
  //       zapierApi
  //         // Add 1 since it's not zero-based and another 1 since we already got the first page
  //         .listZapierApps(100, x + 2)
  //         .then((resp) => resp.data.objects)
  //     )
  //   ).then((otherPages) => {
  //     return [...firstPage, ...flatMap(otherPages)]
  //   })
  // })
})

export const initialZapierAppsState: ZapierAppsState = zapierAppsAdapter.getInitialState({
  loadingStatus: 'not loaded',
  error: null,
})

export const zapierAppsSlice = createSlice({
  name: ZAPIER_APPS_FEATURE_KEY,
  initialState: initialZapierAppsState,
  reducers: {
    add: zapierAppsAdapter.addOne,
    setMany: zapierAppsAdapter.setMany,
    remove: zapierAppsAdapter.removeOne,
    // ...
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchZapierApps.pending, (state: ZapierAppsState) => {
        state.loadingStatus = 'loading'
      })
      .addCase(fetchZapierApps.fulfilled, (state: ZapierAppsState, action) => {
        // zapierAppsAdapter.setAll(state, action.payload)
        state.loadingStatus = 'loaded'
      })
      .addCase(fetchZapierApps.rejected, (state: ZapierAppsState, action) => {
        state.loadingStatus = 'error'
        state.error = action.error.message
      })
  },
})

/*
 * Export reducer for store configuration.
 */
export const zapierAppsReducer = zapierAppsSlice.reducer

/*
 * Export action creators to be dispatched. For use with the `useDispatch` hook.
 *
 * e.g.
 * ```
 * import React, { useEffect } from 'react';
 * import { useDispatch } from 'react-redux';
 *
 * // ...
 *
 * const dispatch = useDispatch();
 * useEffect(() => {
 *   dispatch(zapierAppsActions.add({ id: 1 }))
 * }, [dispatch]);
 * ```
 *
 * See: https://react-redux.js.org/next/api/hooks#usedispatch
 */
export const zapierAppsActions = zapierAppsSlice.actions

/*
 * Export selectors to query state. For use with the `useSelector` hook.
 *
 * e.g.
 * ```
 * import { useSelector } from 'react-redux';
 *
 * // ...
 *
 * const entities = useSelector(selectAllZapierApps);
 * ```
 *
 * See: https://react-redux.js.org/next/api/hooks#useselector
 */
const { selectAll, selectEntities, selectById } = zapierAppsAdapter.getSelectors()

export const getZapierAppsState = (rootState: RootState): ZapierAppsState => rootState[ZAPIER_APPS_FEATURE_KEY]

export const selectAllZapierApps = createSelector(getZapierAppsState, selectAll)
export const selectZapierAppById = (id: EntityId) => createSelector(getZapierAppsState, (state) => selectById(state, id))

export const selectZapierAppsEntities = createSelector(getZapierAppsState, selectEntities)
export const selectZapierAppsLoadingStatus = createSelector(getZapierAppsState, (state) => state.loadingStatus)
export const selectZapierAppsError = createSelector(getZapierAppsState, (state) => state.error)
